import React, { useEffect, useRef, useState } from "react";
import { IconButton, InputAdornment, TextField, Tooltip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { isSensitiveHeader } from "../../services/shared";

interface Props {
  headerName: string;
  value: string;
  onChange: (next: string) => void;
  label?: string;
  size?: "small" | "medium";
  fullWidth?: boolean;
  disabled?: boolean;
  /** Force masking even if header name is not in SENSITIVE_HEADER_PATTERNS (es. detail readonly view). */
  forceMask?: boolean;
  autoHideMs?: number;
}

const DEFAULT_AUTO_HIDE_MS = 5000;

export default function MaskedHeaderField({
  headerName,
  value,
  onChange,
  label = "Valore",
  size = "small",
  fullWidth,
  disabled,
  forceMask,
  autoHideMs = DEFAULT_AUTO_HIDE_MS,
}: Props) {
  const sensitive = forceMask || isSensitiveHeader(headerName);
  const [revealed, setRevealed] = useState(false);
  const hideTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
    };
  }, []);

  useEffect(() => {
    // Reset reveal if header name changes (could become non-sensitive)
    setRevealed(false);
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
  }, [headerName]);

  const handleToggle = () => {
    if (!sensitive) return;
    if (revealed) {
      setRevealed(false);
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
      return;
    }
    setRevealed(true);
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => {
      setRevealed(false);
      hideTimer.current = null;
    }, autoHideMs);
  };

  const shouldMask = sensitive && !revealed;

  return (
    <TextField
      label={label}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      type={shouldMask ? "password" : "text"}
      autoComplete="off"
      InputProps={{
        endAdornment: sensitive ? (
          <InputAdornment position="end">
            <Tooltip title={revealed ? "Nascondi" : "Mostra (5s)"}>
              <IconButton
                size="small"
                onClick={handleToggle}
                edge="end"
                tabIndex={-1}
                aria-label={revealed ? "Nascondi valore" : "Mostra valore"}
              >
                {revealed ? (
                  <VisibilityOffIcon fontSize="small" />
                ) : (
                  <VisibilityIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ) : undefined,
      }}
    />
  );
}
